import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Plus, Search, Undo2, UserRound, X } from 'lucide-react';
import { toast } from 'sonner';
import type { UserType } from '@prisma/client';
import { USER_TYPE_INFO } from '@shared/user-types';
import type { DemoOption, OwnerOption } from '@shared/schemas/admin-access';
import { fetchAdminMe, fetchImpersonationSortFacts } from '../api/admin';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { DemoOptionRow } from './demo/DemoOptionRow';
import { useDemoOptions } from './demo/useDemoOptions';
import { useDemoStartAction } from './demo/useDemoStartAction';
import { useGlobalImpersonationShortcut } from '../hooks/useGlobalImpersonationShortcut';
import {
  STOP_IMPERSONATING,
  describeImpersonationError,
  useSubmitImpersonationMapping,
  useSwitchImpersonation,
} from '../hooks/useImpersonation';
import {
  DELPHI_APP_PATH,
  isPathWithin,
} from '../hooks/useSecretAppShortcuts';
import { useApi } from '../lib/api';
import { AUTOFILL_IGNORE_PROPS } from '../lib/autofill';
import {
  closeImpersonationPalette,
  openImpersonationPalette,
  useImpersonationPaletteStore,
} from '../lib/impersonation-palette-store';
import { MAX_IMPERSONATION_QUICK, readQuickUsers, saveQuickUsers } from '../lib/impersonation-quick';
import { readRecentUsers } from '../lib/impersonation-recents';
import { pushEscapeLayer } from './ui/escape-stack';
import { useModalFocusTrap } from './ui/useModalFocusTrap';
import { emailToDisplayName, getInitials } from '../view-system/utils/displayFormatters';
import { ImpersonationPreviewPane, ReportingLine, impersonationPreviewQuery } from './impersonation/ImpersonationPreviewPane';
import { ROLE_TONE } from './impersonation/role-tone';
import { SortMenu } from './impersonation/SortMenu';
import { SORT_KEYS, readSortChoice, saveSortChoice, sortPeople, sortValueOf, type SortChoice } from './impersonation/sort-people';

/** Until the viewer pins someone, Quick is their last three. */
const QUICK_DEFAULT_COUNT = 3;

// Workflow-facing roles first; Member is the fallback for a null userType.
const ROLE_FILTERS = [
  { filter: 'All', label: 'All' },
  { filter: 'POST_SALES', label: USER_TYPE_INFO.POST_SALES.shortLabel },
  { filter: 'PRE_SALES', label: USER_TYPE_INFO.PRE_SALES.shortLabel },
  { filter: 'SDR', label: USER_TYPE_INFO.SDR.shortLabel },
  { filter: 'REVOPS', label: USER_TYPE_INFO.REVOPS.shortLabel },
  { filter: 'MEMBER', label: USER_TYPE_INFO.MEMBER.shortLabel },
] as const satisfies ReadonlyArray<{ filter: 'All' | UserType; label: string }>;

type RoleFilter = (typeof ROLE_FILTERS)[number]['filter'];

const HINTS = [['↑↓', 'Move'], ['↵', 'View as'], ['1–9', 'Quick'], ['⌥S', 'Sort'], ['esc', 'Close']] as const;

const kbdClass = 'rounded border border-border-primary bg-bg-secondary px-1.5 text-[11px] font-medium text-content-secondary';

const roleOf = (user: OwnerOption): UserType => user.userType ?? 'MEMBER';

export default function GlobalImpersonationPalette() {
  const { pathname } = useLocation();
  const { canImpersonate, canStartDemo } = useUserControlsContext();
  const mayOpen = canImpersonate || canStartDemo;
  const open = useImpersonationPaletteStore((state) => state.open);
  const excluded = isPathWithin(pathname, DELPHI_APP_PATH);

  const togglePalette = useCallback(
    () => (open ? closeImpersonationPalette() : openImpersonationPalette()),
    [open],
  );

  useGlobalImpersonationShortcut({
    enabled: mayOpen && !excluded,
    open,
    onToggle: togglePalette,
  });

  if (!mayOpen || excluded || !open) return null;
  return <ImpersonationPaletteDialog onClose={closeImpersonationPalette} />;
}

function ImpersonationPaletteDialog({ onClose }: { onClose: () => void }) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { impersonation, canImpersonate } = useUserControlsContext();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('All');
  // Who is highlighted, by Slack ID: a late sort reorder must not move the highlight to someone else.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [recentUsers] = useState(readRecentUsers);
  const [pinned, setPinned] = useState(readQuickUsers);
  const [sort, setSortState] = useState(readSortChoice);
  const [sortOpen, setSortOpen] = useState(false);
  // Sorting by last meeting opens the preview's meetings on Last; Next / Last switches it either way.
  const [meetingsTab, setMeetingsTab] = useState<'next' | 'last'>(() => (sort.key === 'lastMeeting' ? 'last' : 'next'));
  const [now] = useState(() => new Date());
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const submitMapping = useSubmitImpersonationMapping();
  const switchImpersonation = useSwitchImpersonation();
  useModalFocusTrap({ open: true, containerRef: dialogRef });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => fetchAdminMe(apiFetch),
    staleTime: Infinity,
  });

  const ownerOptions = useMemo(() => data?.ownerOptions ?? [], [data?.ownerOptions]);
  const bySlackId = useMemo(() => new Map(ownerOptions.map((user) => [user.slackUserId, user])), [ownerOptions]);
  const { data: sortFacts, isError: sortFailed, refetch: retrySortFacts } = useQuery({
    queryKey: ['admin', 'impersonation-sort-facts'],
    queryFn: () => fetchImpersonationSortFacts(apiFetch),
    staleTime: Infinity,
    enabled: canImpersonate && sort.key !== 'name',
    meta: { suppressErrorToast: true },
  });
  const factsById = useMemo(
    () => (sortFacts ? new Map(sortFacts.people.map((fact) => [fact.slackUserId, fact])) : undefined),
    [sortFacts],
  );
  // Until everyone's values land, the list stays A–Z; if they cannot load, it stays A–Z and says so.
  const sortWaiting = canImpersonate && sort.key !== 'name' && !factsById && !sortFailed;
  const showSortValues = canImpersonate && sort.key !== 'name' && !sortFailed;
  const inRole = useCallback(
    (user: OwnerOption) => roleFilter === 'All' || roleOf(user) === roleFilter,
    [roleFilter],
  );
  const query = search.trim().toLowerCase();
  const visibleUsers = useMemo(() => sortPeople(ownerOptions.filter((user) => {
    if (!inRole(user)) return false;
    // The person's own name only: typing a manager's name must not list their whole team.
    return !query || emailToDisplayName(user.ownerEmail).toLowerCase().includes(query);
  }), sort, factsById, (user) => emailToDisplayName(user.ownerEmail)), [ownerOptions, inRole, query, sort, factsById]);
  const visibleDemos = useDemoOptions(data?.demoOptions, query);
  const quickIds = useMemo(() => {
    const ids = pinned ?? recentUsers.slice(0, QUICK_DEFAULT_COUNT).map((user) => user.slackUserId);
    // Someone no longer on the roster has no chip to remove, so they must not hold a Quick slot.
    return data ? ids.filter((id) => bySlackId.has(id)) : ids;
  }, [pinned, recentUsers, data, bySlackId]);
  const quickUsers = useMemo(
    () => quickIds.map((id) => bySlackId.get(id)).filter((user): user is OwnerOption => user !== undefined && inRole(user)),
    [quickIds, bySlackId, inRole],
  );
  // Nobody picked yet, or the pick left the list or filter: the top of the list.
  const selectedPosition = selectedId === null ? -1 : visibleUsers.findIndex((user) => user.slackUserId === selectedId);
  const activeIndex = visibleUsers.length === 0 ? -1 : Math.max(selectedPosition, 0);
  const highlighted = activeIndex >= 0 ? visibleUsers[activeIndex] : undefined;
  const viewingName = impersonation?.active ? emailToDisplayName(impersonation.actingAs) : null;
  const viewingRole = impersonation?.actingAsSlackId ? bySlackId.get(impersonation.actingAsSlackId) : undefined;

  useEffect(() => {
    return pushEscapeLayer(onClose);
  }, [onClose]);

  // The trap focuses the first control, which is Back to me while viewing as someone; typing starts in the search.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Closing the sort menu unmounts the focused item; hand focus back to the search.
  const sortWasOpen = useRef(false);
  useEffect(() => {
    if (sortWasOpen.current && !sortOpen) {
      const active = document.activeElement;
      if (!active || active === document.body || active === dialogRef.current) searchRef.current?.focus();
    }
    sortWasOpen.current = sortOpen;
  }, [sortOpen]);

  // Quick is one keystroke away, so its previews are fetched before anyone points at it.
  const quickKey = quickUsers.map((user) => user.slackUserId).join(',');
  useEffect(() => {
    if (!quickKey) return;
    for (const slackUserId of quickKey.split(',').slice(0, QUICK_DEFAULT_COUNT)) {
      void queryClient.prefetchQuery(impersonationPreviewQuery(apiFetch, slackUserId));
    }
  }, [quickKey, queryClient, apiFetch]);

  const selectUser = async (user: OwnerOption | undefined, href?: string) => {
    if (!user || isSwitching) return;
    setIsSwitching(true);
    try {
      await switchImpersonation(user);
      if (href) window.location.assign(href);
      else window.location.reload();
    } catch (error) {
      toast.error(describeImpersonationError(error));
      setIsSwitching(false);
    }
  };

  const startDemoAs = useDemoStartAction({ onError: (message) => { toast.error(message); } });
  const selectDemo = async (option: DemoOption) => {
    if (isSwitching) return;
    setIsSwitching(true);
    if (!(await startDemoAs(option))) setIsSwitching(false);
  };

  const stopImpersonating = async () => {
    if (isSwitching) return;
    setIsSwitching(true);
    try {
      await submitMapping(STOP_IMPERSONATING);
      window.location.reload();
    } catch (error) {
      toast.error(describeImpersonationError(error));
      setIsSwitching(false);
    }
  };

  const setSort = (choice: SortChoice) => {
    setSortState(choice);
    saveSortChoice(choice);
    setSelectedId(null);
    // A meeting sort opens the preview's meetings on the side it orders by.
    if (choice.key === 'lastMeeting') setMeetingsTab('last');
    if (choice.key === 'nextMeetings') setMeetingsTab('next');
  };

  const setQuick = (ids: string[]) => {
    setPinned(ids);
    saveQuickUsers(ids);
  };
  const canPin = highlighted !== undefined
    && !quickIds.includes(highlighted.slackUserId)
    && quickIds.length < MAX_IMPERSONATION_QUICK;

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // While the sort menu is open the menu owns the keys.
    if (sortOpen) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (visibleUsers.length === 0) return;
      const next = event.key === 'ArrowDown'
        ? (activeIndex + 1) % visibleUsers.length
        : (activeIndex - 1 + visibleUsers.length) % visibleUsers.length;
      setSelectedId(visibleUsers[next].slackUserId);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void selectUser(highlighted);
    }
  };

  // ⌥S and 1–9 work wherever focus is in the palette; the arrows and Enter belong to the search.
  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey && event.code === 'KeyS') {
      event.preventDefault();
      setSortOpen((open) => !open);
      return;
    }
    // 1–9 pick Quick while the search is empty; once typing, digits are search text.
    if (!sortOpen && !search && /^[1-9]$/.test(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const user = quickUsers[Number(event.key) - 1];
      if (!user) return;
      event.preventDefault();
      void selectUser(user);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center px-4 pt-[8vh]">
      <div
        aria-hidden="true"
        data-testid="quick-impersonation-backdrop"
        className="absolute inset-0 bg-bg-scrim"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        onKeyDown={handleDialogKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Quick impersonation"
        className="relative z-10 flex max-h-[84vh] w-full max-w-[820px] flex-col overflow-hidden rounded-2xl border border-border-primary bg-bg-primary text-content-primary shadow-xl"
      >
        {viewingName && (
          <div className="flex items-center gap-2.5 border-b border-border-orange-light bg-bg-orange-light py-2 pl-4 pr-3">
            <span className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${ROLE_TONE[viewingRole ? roleOf(viewingRole) : 'MEMBER'].avatar}`}>
              {getInitials(viewingName)}
            </span>
            <span className="min-w-0 truncate text-[13px] text-content-secondary">
              You&apos;re viewing Terra as <span className="font-semibold text-content-primary">{viewingName}</span>
            </span>
            <button
              type="button"
              disabled={isSwitching}
              onClick={() => { void stopImpersonating(); }}
              className="ml-auto flex shrink-0 items-center gap-1.5 rounded-md border border-border-orange-light bg-bg-primary px-2.5 py-1 text-xs font-semibold text-text-orange transition-colors hover:bg-bg-primary-hover disabled:opacity-50"
            >
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
              Back to me
            </button>
          </div>
        )}

        <div className="flex items-center gap-3 px-5 pb-2.5 pt-4">
          <Search className="h-5 w-5 shrink-0 text-icon-subtle" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            {...AUTOFILL_IGNORE_PROPS}
            aria-label="Search impersonable users"
            placeholder="Who do you want to see Terra as?"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelectedId(null);
            }}
            onKeyDown={handleListKeyDown}
            className="min-w-0 flex-1 bg-transparent text-lg font-medium text-content-primary outline-none placeholder:text-content-placeholder"
          />
          <span className="hidden rounded border border-border-primary px-1.5 py-0.5 text-[11px] font-medium text-content-tertiary sm:inline">
            esc
          </span>
        </div>

        {!query && (
          <div data-testid="impersonation-quick" className="flex flex-wrap items-center gap-2 px-5 pb-3">
            <span className="mr-0.5 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">Quick</span>
            {quickUsers.map((user, i) => {
              const name = emailToDisplayName(user.ownerEmail);
              return (
                <span key={user.slackUserId} className="group relative inline-flex">
                  <button
                    type="button"
                    aria-label={`Quick impersonate ${name}`}
                    aria-keyshortcuts={String(i + 1)}
                    disabled={isSwitching}
                    onClick={() => { void selectUser(user); }}
                    className="inline-flex h-8 items-center gap-2 rounded-full border border-border-primary bg-bg-primary pl-1 pr-2 transition-colors hover:bg-bg-secondary disabled:opacity-50"
                  >
                    <span className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-bold ${ROLE_TONE[roleOf(user)].avatar}`}>
                      {getInitials(name)}
                    </span>
                    <span className="whitespace-nowrap text-[13px] font-semibold text-content-primary">{name}</span>
                    <kbd className={kbdClass}>{i + 1}</kbd>
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${name} from Quick`}
                    onClick={() => setQuick(quickIds.filter((id) => id !== user.slackUserId))}
                    className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-border-primary bg-bg-primary text-content-tertiary opacity-0 transition-opacity hover:text-content-primary focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    <X className="h-2.5 w-2.5" aria-hidden="true" />
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              disabled={!canPin}
              aria-label={highlighted ? `Pin ${emailToDisplayName(highlighted.ownerEmail)} to Quick` : 'Pin someone to Quick'}
              title={highlighted ? `Pin ${emailToDisplayName(highlighted.ownerEmail)} to Quick` : undefined}
              onClick={() => { if (highlighted) setQuick([...quickIds, highlighted.slackUserId]); }}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-border-strong text-content-tertiary transition-colors hover:text-content-primary disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-1 border-b border-border-secondary px-4 pb-2.5">
        <div aria-label="Filter users by role" className="flex flex-wrap gap-1">
          {ROLE_FILTERS.map(({ filter, label }) => {
            const active = roleFilter === filter;
            const count = filter === 'All' ? ownerOptions.length : ownerOptions.filter((user) => roleOf(user) === filter).length;
            return (
              <button
                key={filter}
                type="button"
                aria-label={`Filter by role: ${label}`}
                aria-pressed={active}
                onClick={() => {
                  setRoleFilter(filter);
                  setSelectedId(null);
                }}
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                  active ? 'bg-bg-secondary text-content-primary' : 'text-content-secondary hover:bg-bg-secondary'
                }`}
              >
                <span aria-hidden="true" className={`h-[7px] w-[7px] rounded-full ${filter === 'All' ? 'bg-content-tertiary' : ROLE_TONE[filter].dot}`} />
                {label}
                <span className="font-medium tabular-nums text-content-tertiary">{count}</span>
              </button>
            );
          })}
        </div>
        <SortMenu
          value={sort}
          onChange={setSort}
          open={sortOpen}
          onOpenChange={setSortOpen}
          loading={sortWaiting}
          failed={sort.key !== 'name' && sortFailed}
          onRetry={() => { void retrySortFacts(); }}
        />
        </div>

        <div className="grid h-[404px] min-h-0 shrink grid-cols-[300px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
          <div
            role="listbox"
            aria-label="Impersonable users"
            className="flex min-h-0 flex-col gap-px overflow-y-auto border-r border-border-secondary p-2"
          >
            {visibleDemos.length > 0 && (
              <div role="group" aria-labelledby="demo-identities-heading" className="flex flex-col gap-px pb-1.5">
                <span id="demo-identities-heading" className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
                  Demo identities
                </span>
                {visibleDemos.map((option) => (
                  <DemoOptionRow
                    key={option.slackUserId}
                    option={option}
                    disabled={isSwitching}
                    onSelect={(picked) => { void selectDemo(picked); }}
                  />
                ))}
              </div>
            )}
            {canImpersonate && (
            <span className="flex items-baseline justify-between gap-2 px-2 pb-1 pt-1.5 text-[11px] text-content-tertiary">
              <span className="font-semibold uppercase tracking-wider">
                {query ? `${visibleUsers.length} ${visibleUsers.length === 1 ? 'match' : 'matches'}` : `Everyone · ${visibleUsers.length}`}
              </span>
              {showSortValues && <span className="whitespace-nowrap font-medium">{SORT_KEYS[sort.key].unit}</span>}
            </span>
            )}
            {visibleUsers.map((user, itemIndex) => {
              const name = emailToDisplayName(user.ownerEmail);
              const role = roleOf(user);
              const selected = activeIndex === itemIndex;
              return (
                <button
                  key={user.slackUserId}
                  type="button"
                  role="option"
                  aria-label={`Impersonate ${name} (${user.ownerEmail})`}
                  aria-selected={selected}
                  disabled={isSwitching}
                  onMouseEnter={() => setSelectedId(user.slackUserId)}
                  onClick={() => { void selectUser(user); }}
                  className={`flex min-h-[46px] w-full shrink-0 items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors disabled:opacity-50 ${
                    selected ? 'bg-bg-selected' : 'hover:bg-bg-secondary'
                  }`}
                >
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${ROLE_TONE[role].avatar}`}>
                    {getInitials(name)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-px">
                    <span className={`truncate text-[13.5px] font-semibold ${selected ? 'text-content-selected' : 'text-content-primary'}`}>{name}</span>
                    {user.reportingLine?.length ? (
                      <span className="truncate text-[11.5px] text-content-tertiary">
                        <ReportingLine managers={user.reportingLine} />
                      </span>
                    ) : null}
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-px pl-2">
                    {showSortValues && (sortWaiting ? (
                      <span aria-hidden="true" className="my-[3px] h-2.5 w-[34px] rounded bg-bg-tertiary" />
                    ) : (
                      <SortValue value={sortValueOf(sort.key, factsById?.get(user.slackUserId), now)} />
                    ))}
                    <span className={`text-[11px] font-medium ${ROLE_TONE[role].text}`}>{USER_TYPE_INFO[role].shortLabel}</span>
                  </span>
                </button>
              );
            })}

            {isLoading && (
              <div className="px-4 py-8 text-center text-sm text-content-tertiary">Loading users…</div>
            )}
            {isError && (
              <div className="px-4 py-8 text-center text-sm text-content-danger">Couldn&apos;t load impersonable users.</div>
            )}
            {canImpersonate && !isLoading && !isError && visibleUsers.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-content-tertiary">
                <UserRound className="h-6 w-6" aria-hidden="true" />
                <span className="text-sm">No users match.</span>
              </div>
            )}
          </div>

          {highlighted ? (
            <ImpersonationPreviewPane
              user={highlighted}
              name={emailToDisplayName(highlighted.ownerEmail)}
              disabled={isSwitching}
              onSelect={(href) => { void selectUser(highlighted, href); }}
              meetingsTab={meetingsTab}
              onMeetingsTab={setMeetingsTab}
            />
          ) : (
            <div className="bg-bg-secondary" />
          )}
        </div>

        <div className="flex items-center gap-3.5 border-t border-border-secondary px-[18px] py-2.5 text-xs text-content-tertiary">
          {HINTS.map(([key, label]) => (
            <span key={key} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <kbd className={kbdClass}>{key}</kbd>
              {label}
            </span>
          ))}
          <span className="ml-auto hidden truncate md:inline">Quick starts as your last three · + pins the highlighted person</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SortValue({ value }: { value: { text: string; muted: boolean } | null }) {
  if (!value) return null;
  return (
    <span className={`whitespace-nowrap text-xs font-medium tabular-nums ${value.muted ? 'text-content-tertiary' : 'text-content-secondary'}`}>
      {value.text}
    </span>
  );
}
